/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EXECUTE_DEPOSIT } from '@comeon/actions-public';

import { selectCurrency, selectPlayerId } from '~/components/account/ducks';
import { IsGermanRegulatedFranchise, selectSwishQRCodeFlow } from '~/components/brand/ducks/selectors';
import { currentGameSelector } from '~/components/casino-shared/game-loader/ducks/selectors';
import IconButtonClose from '~/components/misc/icon-button-close';
import SchufaConsentPopup from '~/components/payment/deposit/components/schufa-popup-cashier';
import { getCVVBypass } from '~/components/payment/ducks/payment';
import { selectDepositType } from '~/components/payment/ducks/quick-deposit-duck';
import { loadPaypalScriptAsync } from '~/components/payment/hooks/use-paypal-order-ref';
import useSeonPayment from '~/components/payment/hooks/useSeonPayment';
import {
	ExtraMethodParams,
	InstantDepositData,
	ProviderLimitsData,
	QuickDepositProvider,
	Result,
	SelectedMethod,
	TAction,
	TriggerViewFn
} from '~/components/payment/quick-deposit-beta/types';
import {
	API_LOADING,
	CREDITCARD,
	DEPOSIT_REDIRECT_CONFIRM,
	LOADING,
	MAIN_CONTENT,
	PAYPAL,
	PROMPT_CVV,
	PROMPT_ONECLICK,
	QD_EXECUTE,
	SHUFA_CONSENT,
	SWISH,
	textKeysQDAmountScreen as textKeys
} from '~/components/payment/quick-deposit-beta/utils/constants';
import executeInstantDeposit, { checkSchufaConsent } from '~/components/payment/quick-deposit-beta/utils/execute-util';
import { getLogo, renderAccountDetails } from '~/components/payment/quick-deposit-beta/utils/qd-helpers';
import {
	Header,
	InfoText,
	PaymentButtonSecondary,
	StyledAmountInput
} from '~/components/payment/quick-deposit-beta/utils/qd-styled-utils';
import { useQDNotifications } from '~/components/payment/quick-deposit-beta/utils/use-quick-deposit-hooks';
import useTranslations from '~/components/translation/use-translations';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import { RootState } from '~/store/types';

import QDCVVscreen from './qd-cvv-modal';
import QDMethodsCollapse from './qd-payment-methods-collapse';
import QDPaypalscreen from './qd-paypal-modal';

type Props = {
	addView: (...args: string[]) => TAction;
	hideView: (...args: string[]) => TAction;
	instantDepositData: InstantDepositData;
	providerLimitsData: ProviderLimitsData;
	redirectToPayments: () => void;
	selectedMethod: SelectedMethod;
	setCrossLimit: (value: boolean) => void;
	setRGmessageState: (value: boolean) => void;
	setSelectedMethod: (provider: QuickDepositProvider) => void;
	setshowKYCmessage: (value: boolean) => void;
	setSchufaError: (value: string) => void;
	setlugasLimit: (value: boolean) => void;
	updateAmountVal: number;
	updateViews: (...args: ReturnType<TriggerViewFn>[]) => TAction;
	views: string[];
};

const QDAmountContainer = ({
	addView,
	hideView,
	instantDepositData: { depositInfo, depositInfoStatus },
	providerLimitsData,
	redirectToPayments,
	selectedMethod,
	setCrossLimit,
	setRGmessageState,
	setSelectedMethod,
	setSchufaError,
	setlugasLimit,
	setshowKYCmessage,
	updateAmountVal,
	updateViews,
	views
}: Props) => {
	const dispatch = useDispatch();

	const [prompt, setPrompt] = useState<string | null>('');
	const [amount, setAmount] = useState(0);
	const [limitInfo, setLimitInfo] = useState('');
	const [disableSubmit, setDisableStatus] = useState(false);
	const [methodListToggle, toggleMethodList] = useState(false);
	const [executeOnMethods, setExecuteOnMethodsFlag] = useState(false);

	const paypalSelected = useRef(false);

	const { showErrorNotification } = useQDNotifications();
	const closeCurrentContext = useCloseCurrentContext();
	const currency = useSelector<RootState, string>(selectCurrency);
	const playerId = useSelector<RootState, string>(selectPlayerId);
	const swishQRCodeFlow = useSelector(selectSwishQRCodeFlow);
	const isGermanFranchise = useSelector(IsGermanRegulatedFranchise);

	let bypassCvv: boolean;

	const hasCasinoGame = !!useSelector(currentGameSelector);

	const depositType = useSelector(selectDepositType);

	const selectedAccountDetails = renderAccountDetails(selectedMethod, 'button');
	const { isLoadingTranslations, translations: t } = useTranslations(textKeys);
	const { addSeonEvent } = useSeonPayment();
	const {
		sum_limit: sumLimit,
		sum_min: sumMin,
		sum_max: sumMax,
		formattedSumMin,
		formattedSumMax,
		paymentMethodId,
		providerId
	} = providerLimitsData;

	const checkLimitsStatus = (amountValue: number): boolean => {
		if (sumLimit && amountValue > sumLimit) {
			setCrossLimit(true);
			setRGmessageState(true);
		}

		switch (true) {
			case amountValue < sumMin:
				setLimitInfo(`${t.AMOUNT_MIN} ${formattedSumMin}`);
				setDisableStatus(true);

				return false;

			case amountValue > sumMax:
				setLimitInfo(`${t.AMOUNT_MAX} ${formattedSumMax}`);
				setDisableStatus(true);

				return false;

			default:
				setLimitInfo('');
				setDisableStatus(false);

				return true;
		}
	};

	const validateAndSetAmount = (value: number) => {
		setAmount(value);
		checkLimitsStatus(value);
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const inputVal = event.target?.value;
		const formattedVal = Number(inputVal);

		if (!inputVal) setDisableStatus(true);

		if (!Number.isNaN(formattedVal)) validateAndSetAmount(formattedVal);
	};

	const updateViewOnAmount = (inputVal: number) => {
		switch (true) {
			case inputVal < sumMin:
				setRGmessageState(true);
				break;

			case sumMax < sumMin:
				setRGmessageState(true);
				break;

			case inputVal > sumMax:
				validateAndSetAmount(sumMax);
				break;

			default:
				validateAndSetAmount(inputVal);
				break;
		}
	};

	const selectMethodAndExecute = (provider: QuickDepositProvider) => {
		setSelectedMethod(provider);

		const extraParams = provider?.extraParameters;
		const isOneClickEnabled =
			extraParams?.promptModalCode === PROMPT_ONECLICK && extraParams?.activePaypalBillingAgreementTokenId;

		toggleMethodList(!methodListToggle);

		if (isOneClickEnabled) {
			paypalSelected.current = true;
			return;
		}

		paypalSelected.current = false;

		setExecuteOnMethodsFlag(true);
	};

	const executeQDDeposit = async (extraMethodParams: ExtraMethodParams) => {
		updateViews(addView(LOADING));

		await addSeonEvent();

		const result = (await executeInstantDeposit(
			{
				...selectedMethod,
				...extraMethodParams,
				amount,
				depositType
			},
			dispatch
		)) as Result;

		if (result.successful) {
			dispatch({
				type: EXECUTE_DEPOSIT,
				payload: {
					result,
					spec: { exe_pending: false }
				}
			});

			updateViews(hideView(MAIN_CONTENT, LOADING));
			updateViews(addView(QD_EXECUTE));
		} else {
			setPrompt(null);
			updateViews(hideView(LOADING));
			const { availableDepositAmount } = result;
			if (typeof availableDepositAmount !== 'undefined' && availableDepositAmount !== null) {
				setlugasLimit(true);
				return setRGmessageState(true);
			}
			const errorMessage = result.errorMessage
				? `${result.errorMessage.title} - ${result.errorMessage.message}`
				: t.SYSTEM_ERROR_GENERAL;
			showErrorNotification(errorMessage);
		}
	};

	const getExtraParamsFromComponent = async () => {
		const { extraParameters, CVV2Required, playerCardData } = selectedMethod;
		const { promptModalCode, activePaypalBillingAgreementTokenId } = extraParameters;
		const oneClickDisabled = !activePaypalBillingAgreementTokenId;

		switch (selectedMethod.paymentMethodName) {
			case SWISH:
				return { params: { swishQRCodeFlow }, shouldExecuteDeposit: true };

			case CREDITCARD:
				// Check for CVV2 prompt condition before proceeding with execute
				if (CVV2Required) {
					updateViews(addView(LOADING));
					bypassCvv = (await getCVVBypass(
						dispatch,
						playerCardData.id,
						amount,
						providerId,
						paymentMethodId
					)) as boolean;
					updateViews(hideView(LOADING));
					if (!bypassCvv) {
						setPrompt(PROMPT_CVV as string);
						return { params: {}, shouldExecuteDeposit: false };
					}
				}

				return { params: { bypassCvv }, shouldExecuteDeposit: true };

			case PAYPAL: {
				// Check for PayPal prompt condition before proceeding with execute
				if (promptModalCode === PROMPT_ONECLICK && oneClickDisabled) {
					setPrompt(PROMPT_ONECLICK as string);
					return { params: {}, shouldExecuteDeposit: false };
				}

				/** extraParams for PAYPAL is set here incase of OneClickEnabled,
				 * oneClickDisabled flow will set these values in QDPaypalscreen */
				updateViews(addView(LOADING));
				const OrderFuniquedID = await loadPaypalScriptAsync();
				return { params: { enableOneClick: true, OrderFuniquedID, cu: currency }, shouldExecuteDeposit: true };
			}

			default:
				return { params: {}, shouldExecuteDeposit: true };
		}
	};

	const initiateDeposit = async () => {
		const { params, shouldExecuteDeposit } = await getExtraParamsFromComponent();

		if (shouldExecuteDeposit) {
			return executeQDDeposit(params);
		}
	};

	const handleDepositExecution = async () => {
		const { needValidation } = selectedMethod.depositKycData || {};

		if (needValidation) {
			return setshowKYCmessage(true);
		}

		if (isGermanFranchise) {
			updateViews(addView(LOADING));

			const schufaConsentNeeded = await checkSchufaConsent(amount, playerId);
			updateViews(hideView(LOADING));
			if (schufaConsentNeeded) {
				return setPrompt(SHUFA_CONSENT as string);
			}
		}

		return initiateDeposit();
	};

	useEffect(() => {
		if (isLoadingTranslations || depositInfoStatus === API_LOADING) {
			updateViews(addView(LOADING));
		} else {
			updateViews(hideView(LOADING));
		}
	}, [isLoadingTranslations]);

	useEffect(() => {
		if (!isLoadingTranslations) {
			setLimitInfo(t.EDIT_AMOUNT);
		}
	}, [isLoadingTranslations, t]);

	useEffect(() => {
		if (
			updateAmountVal !== Number(amount) &&
			Object.keys(providerLimitsData).length &&
			!executeOnMethods &&
			!paypalSelected.current
		) {
			const val = updateAmountVal < sumMin ? sumMin : updateAmountVal;

			updateViewOnAmount(val);
		}

		if (executeOnMethods && checkLimitsStatus(amount)) {
			handleDepositExecution().catch(error =>
				// eslint-disable-next-line no-console
				console.error('quick-deposit-amount-screen => handleExecuteDeposit ~ 335 line', error)
			);
		}
	}, [updateAmountVal, providerLimitsData]);

	const handleMoreMethodsClick = useCallback(() => {
		if (hasCasinoGame) {
			updateViews(addView(DEPOSIT_REDIRECT_CONFIRM));
		} else {
			redirectToPayments();
		}
	}, [hasCasinoGame]);

	const renderContent = () => {
		const amountValue = amount === 0 ? '' : amount.toString();

		switch (prompt) {
			case PROMPT_CVV:
				return <QDCVVscreen views={views} translation={t} executeQDDeposit={executeQDDeposit} />;
			case PROMPT_ONECLICK:
				return <QDPaypalscreen views={views} translation={t} executeQDDeposit={executeQDDeposit} />;
			case SHUFA_CONSENT:
				return (
					<SchufaConsentPopup
						onSuccess={initiateDeposit}
						amount={amount}
						status={{ methodId: selectedMethod?.depositMethodId, sum_min: sumMin }}
						onReject={() => setPrompt(null)}
						setSchufaError={setSchufaError}
						qdDepositFlow
					/>
				);
			default:
				return (
					<>
						<div className="u-width-90 u-mt2">
							<StyledAmountInput
								id="amountInput"
								type="tel"
								disabled={views.includes(LOADING)}
								value={amountValue}
								automationClass=""
								className="button u-mb2"
								data-map="phone"
								onChange={handleChange}
								placeholder={t.AMOUNT}
							/>
						</div>
						<InfoText>{limitInfo}</InfoText>
						<PaymentButtonSecondary
							smallfont
							disabled={disableSubmit || views.includes(LOADING)}
							// eslint-disable-next-line @typescript-eslint/no-misused-promises
							onClick={handleDepositExecution}
							type="submit"
							className="button u-mb1 u-mt1"
							title="title"
							dataAt="mobile-quick-deposit-button"
						>
							{t.QD_DEPOSIT_BUTTON}
							{selectedMethod && getLogo(selectedMethod, true)}
							{selectedAccountDetails?.accountInfo}
						</PaymentButtonSecondary>
						<QDMethodsCollapse
							views={views}
							translations={t}
							depositInfo={depositInfo}
							selectedMethod={selectedMethod}
							methodListToggle={methodListToggle}
							toggleMethodList={toggleMethodList}
							selectMethodAndExecute={selectMethodAndExecute}
							onMoreMethodsClick={handleMoreMethodsClick}
						/>
					</>
				);
		}
	};

	return (
		<>
			<Header className="page-head__left-side">
				<IconButtonClose
					className="at-icon-close page-head__close"
					onClick={closeCurrentContext}
					type="button"
				/>
			</Header>
			{renderContent()}
		</>
	);
};

export default QDAmountContainer;
